import { useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "~/components/common/base-link/context";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { Event } from "~/components/tracking/google-analytics/types";
import { topic, tracking } from "~/data";
import { ServiceJournalism } from "~/data/tracking/module";
import type { thingsToDoQueue$key } from "~/queries/__generated__/thingsToDoQueue.graphql";

import {
  Container,
  StyledContentItemHomeSecondary,
  ThingsToDoItems,
  Title,
  TitleBasLink,
} from "./styles";
type Props = {
  className?: string;
  reference: thingsToDoQueue$key;
};

export const HomeThingsToDo: FunctionComponent<Props> = ({ className, reference: reference_ }) => {
  const data = useFragment(
    graphql`
      fragment thingsToDoQueue on Query {
        thingToDoQueue: queue(filter: { name: "homepage_service_journalism_hk" }) {
          settings
          items(first: 3) {
            edges {
              node {
                ... on Content {
                  entityId
                  ...homeSecondaryContentItemContent
                }
              }
            }
          }
        }
      }
    `,
    reference_,
  );

  const defaultTitle = "THINGS TO DO: HONG KONG";
  const customText =
    (data?.thingToDoQueue?.settings as { custom_text?: string } | undefined)?.custom_text ??
    defaultTitle;
  const thingsToItems = data?.thingToDoQueue?.items?.edges;
  const isDefaultTitle = customText.toUpperCase() === defaultTitle;

  const impressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/things_to_do_hk",
      category: "Homepage Depth",
    }),
    [],
  );
  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection<
    Event,
    null
  >({
    gaTrackingHandler: sendGATracking,
    getEvent: impressionEvent,
    options: { isSendGATracking: true, shouldSendOnce: true },
  });

  if (thingsToItems?.length === 0) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.ThingsToDo,
      }}
    >
      <Container className={className}>
        {isDefaultTitle ? (
          <TitleBasLink
            as={topic.whatToDoInHongKong.urlAlias}
            pathname={topic.whatToDoInHongKong.urlAlias}
            query={{ module: ServiceJournalism }}
          >
            <Title>{customText}</Title>
          </TitleBasLink>
        ) : (
          <Title>{customText}</Title>
        )}
        <ThingsToDoItems ref={captureTrackImpressionEventTargetElement}>
          {thingsToItems?.map(({ node }) => (
            <StyledContentItemHomeSecondary
              key={node.entityId}
              reference={node}
              withImage={true}
              withSection={true}
              onClick={entityId => {
                sendGATracking({
                  action: "Click/things_to_do_hk",
                  category: "Homepage Depth",
                  label: entityId,
                });
              }}
            />
          ))}
        </ThingsToDoItems>
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeThingsToDo.displayName = "HomeThingsToDo";
